import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IRegisteredApplicationsService } from '../../interfaces/services/registered-applications-service.interface';
import {
  CreateRegisteredApplicationRequest,
  RegisteredApplication,
  RegisteredApplicationTopics,
  RegisteredApplicationsListRole,
} from '../../interfaces/state/entities/registered-application.interface';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class RegisteredApplicationsService implements IRegisteredApplicationsService {
  private http = inject(HttpClient);
  private apiUrl = environment.scitRegisteredApplicationsUrl;

  getRegisteredApplications(role: RegisteredApplicationsListRole = 'All'): Observable<RegisteredApplication[]> {
    const params = new HttpParams().set('role', role);
    return this.http.get<unknown>(this.apiUrl, { params }).pipe(map((body) => this.normalize(body)));
  }

  createRegisteredApplication(request: CreateRegisteredApplicationRequest): Observable<void> {
    return this.http.post<void>(this.apiUrl, this.buildCreateBody(request));
  }

  private buildCreateBody(request: CreateRegisteredApplicationRequest): Record<string, unknown> {
    const body: Record<string, unknown> = {
      clientId: request.clientId.trim(),
      name: request.name.trim(),
      role: request.role,
    };
    const topics = this.normalizeTopics(request.topics);
    if (topics) {
      body['topics'] = topics;
    }
    return body;
  }

  private normalizeTopics(
    topics?: RegisteredApplicationTopics
  ): RegisteredApplicationTopics | undefined {
    if (!topics) {
      return undefined;
    }
    const drawingProcessed = topics.drawingProcessed?.trim();
    const captureProcessed = topics.captureProcessed?.trim();
    if (!drawingProcessed && !captureProcessed) {
      return undefined;
    }
    return {
      ...(drawingProcessed ? { drawingProcessed } : {}),
      ...(captureProcessed ? { captureProcessed } : {}),
    };
  }

  deleteRegisteredApplication(registeredApplicationId: string): Observable<void> {
    const url = `${this.apiUrl.replace(/\/$/, '')}/${encodeURIComponent(registeredApplicationId)}`;
    return this.http.delete<void>(url);
  }

  private normalize(body: unknown): RegisteredApplication[] {
    let rawList: unknown[] = [];

    if (Array.isArray(body)) {
      rawList = body;
    } else if (body && typeof body === 'object') {
      const o = body as Record<string, unknown>;
      if (Array.isArray(o['applications'])) {
        rawList = o['applications'] as unknown[];
      } else if (Array.isArray(o['registeredApplications'])) {
        rawList = o['registeredApplications'] as unknown[];
      }
    }

    return rawList.map((item) => this.mapItem(item)).filter((a): a is RegisteredApplication => a !== null);
  }

  private mapItem(item: unknown): RegisteredApplication | null {
    if (!item || typeof item !== 'object') {
      return null;
    }
    const o = item as Record<string, unknown>;
    const registeredApplicationId =
      o['registeredApplicationId'] ??
      o['RegisteredApplicationId'] ??
      o['id'] ??
      o['Id'];
    const clientId = o['clientId'] ?? o['ClientId'];
    const name = o['name'] ?? o['Name'];
    if (registeredApplicationId == null || name == null) {
      return null;
    }

    let topics: RegisteredApplicationTopics | undefined;
    const rawTopics = o['topics'];
    if (rawTopics && typeof rawTopics === 'object') {
      const t = rawTopics as Record<string, unknown>;
      topics = {
        drawingProcessed:
          t['drawingProcessed'] != null ? String(t['drawingProcessed']) : undefined,
        captureProcessed:
          t['captureProcessed'] != null ? String(t['captureProcessed']) : undefined,
      };
      if (!topics.drawingProcessed && !topics.captureProcessed) {
        topics = undefined;
      }
    }

    let roles: string[] | undefined;
    if (Array.isArray(o['roles'])) {
      roles = (o['roles'] as unknown[]).map((r) => String(r));
      if (roles.length === 0) {
        roles = undefined;
      }
    }

    const createdAt = o['createdAt'] != null ? String(o['createdAt']) : undefined;

    const id = String(registeredApplicationId);
    const resolvedClientId =
      clientId != null && String(clientId).trim() !== '' ? String(clientId).trim() : id;

    return {
      id,
      ...(resolvedClientId !== id ? { clientId: resolvedClientId } : {}),
      name: String(name),
      ...(createdAt ? { createdAt } : {}),
      ...(roles ? { roles } : {}),
      ...(topics ? { topics } : {}),
    };
  }
}
